import { rpx } from 'client/lib/rpx-client';
import { useEffect, useState } from 'preact/hooks';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { showError } from '@components/app-error';
import { BtnSecondary } from '@components/buttons';

dayjs.extend(utc);

type Result = Awaited<ReturnType<typeof rpx.aipBillingTransfer.getAipBillingTransfer>>;

// Far enough out that the member has time to enter a card, and past Stripe's
// 48-hour floor on a trial end.
const DEFAULT_DAYS_AHEAD = 30;

function defaultFirstCharge() {
  return dayjs.utc().add(DEFAULT_DAYS_AHEAD, 'day').format('YYYY-MM-DD');
}

function formatDate(date: string) {
  return dayjs.utc(`${date}T00:00:00Z`).format('MMM D, YYYY');
}

function formatPrice(price: { priceInCents: number; interval?: string }) {
  const dollars = price.priceInCents / 100;
  const amount = `$${dollars % 1 === 0 ? dollars.toFixed(0) : dollars.toFixed(2)}`;
  if (price.interval === 'month') {
    return `${amount}/month`;
  }
  if (price.interval === 'year') {
    return `${amount}/year`;
  }
  return amount;
}

/**
 * Move a member who pays through Ontraport onto app billing. Shown only for
 * `op_billed` members: everyone else either already pays here or has no
 * membership to move.
 */
export function BillingTransferCard(props: { userId: UUID }) {
  const [result, setResult] = useState<Result | undefined>(undefined);
  const [firstChargeOn, setFirstChargeOn] = useState(defaultFirstCharge());
  const [isSaving, setIsSaving] = useState(false);

  async function load() {
    try {
      setResult(await rpx.aipBillingTransfer.getAipBillingTransfer({ userId: props.userId }));
    } catch (err) {
      showError(err);
    }
  }

  useEffect(() => {
    let cancelled = false;
    rpx.aipBillingTransfer
      .getAipBillingTransfer({ userId: props.userId })
      .then((r) => {
        if (!cancelled) {
          setResult(r);
          // Only carry a still-usable date into the picker. Re-filling a date
          // that has passed makes "re-issue" look like a click and fail with a
          // 422.
          if (r.transfer && !r.transfer.completedAt && !r.isExpired) {
            setFirstChargeOn(r.transfer.firstChargeOn);
          }
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [props.userId]);

  if (!result || !result.eligible) {
    return undefined;
  }

  const transfer = result.transfer;
  const isPending = !!transfer && !transfer.completedAt && !transfer.canceledAt;

  async function issue() {
    setIsSaving(true);
    try {
      await rpx.aipBillingTransfer.issueAipBillingTransfer({
        userId: props.userId,
        firstChargeOn,
      });
      await load();
    } catch (err) {
      showError(err);
    } finally {
      setIsSaving(false);
    }
  }

  async function withdraw() {
    setIsSaving(true);
    try {
      await rpx.aipBillingTransfer.cancelAipBillingTransfer({ userId: props.userId });
      await load();
    } catch (err) {
      showError(err);
      // The refusal that matters here is "they have already entered a card",
      // which also completed the transfer — reload so the panel stops saying
      // it is waiting for one.
      await load();
    } finally {
      setIsSaving(false);
    }
  }

  if (transfer?.completedAt) {
    return (
      <section>
        <h3 class="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
          App billing
        </h3>
        <p class="text-sm text-gray-700">
          Moved to app billing {dayjs(transfer.completedAt).format('MMM D, YYYY')}. First charge{' '}
          {formatDate(transfer.firstChargeOn)}.
        </p>
      </section>
    );
  }

  return (
    <section>
      <h3 class="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
        Move to app billing
      </h3>
      {isPending && !result.isExpired ? (
        <div class="flex flex-col gap-2">
          <p class="text-sm text-gray-700">
            Waiting for a card. First charge {formatDate(transfer!.firstChargeOn)}. They see the
            card form on their billing page.
          </p>
          <div>
            <BtnSecondary onClick={withdraw} disabled={isSaving}>
              Withdraw
            </BtnSecondary>
          </div>
        </div>
      ) : (
        <div class="flex flex-col gap-2">
          {isPending && result.isExpired && (
            <p class="text-sm text-amber-800">
              First charge {formatDate(transfer!.firstChargeOn)} has passed and the form is hidden
              from them. Pick a new date.
            </p>
          )}
          <p class="text-sm text-gray-700">
            They pay in Ontraport. Pick the date of their first charge here, then cancel the
            Ontraport recurring once they have entered a card.
            {result.price && <> Moves them to {formatPrice(result.price)}.</>}
          </p>
          <label class="flex items-center gap-2 text-sm text-gray-700">
            First charge
            <input
              type="date"
              class="rounded border border-gray-300 px-2 py-1 text-sm"
              value={firstChargeOn}
              disabled={isSaving}
              onInput={(e) => setFirstChargeOn((e.target as HTMLInputElement).value)}
            />
          </label>
          <div>
            <BtnSecondary onClick={issue} disabled={isSaving || !firstChargeOn}>
              {isSaving ? 'Saving…' : 'Ask them for a card'}
            </BtnSecondary>
          </div>
        </div>
      )}
    </section>
  );
}
